'use client';

import type { TranslationKey } from '@freedraw/i18n';
import { useAppTranslation } from '@freedraw/i18n/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  ArrowRight,
  Code2,
  GraduationCap,
  Lightbulb,
  ListTodo,
  Palette,
  Sparkles,
  Users,
  type LucideIcon
} from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';

import { DiagramPreview, type DiagramKind } from '@/components/landing/landing-diagrams';
import { DISPLAY_FONT } from '@/components/landing/landing-fonts';
import { LandingParallaxShape } from '@/components/landing/landing-parallax-shapes';
import { BRAND } from '@/theme';

const INK = BRAND.ink;
const MUTED = '#596275';
const ACCENT = BRAND.pinkStrong;

type UseCase = {
  id: string;
  icon: LucideIcon;
  diagram: DiagramKind;
  title: TranslationKey;
  summary: TranslationKey;
  prompt: TranslationKey;
  labels: TranslationKey;
};

const USE_CASES: UseCase[] = [
  {
    id: 'study',
    icon: GraduationCap,
    diagram: 'mindmap',
    title: 'landing.v2Study',
    summary: 'landing.v2StudyDescription',
    prompt: 'landing.v2StudyPrompt',
    labels: 'landing.v2StudyLabels'
  },
  {
    id: 'ideas',
    icon: Lightbulb,
    diagram: 'mindmap',
    title: 'landing.v2Ideas',
    summary: 'landing.v2IdeasDescription',
    prompt: 'landing.v2IdeasPrompt',
    labels: 'landing.v2IdeasLabels'
  },
  {
    id: 'work',
    icon: ListTodo,
    diagram: 'gantt',
    title: 'landing.v2Work',
    summary: 'landing.v2WorkDescription',
    prompt: 'landing.v2WorkPrompt',
    labels: 'landing.v2WorkLabels'
  },
  {
    id: 'design',
    icon: Palette,
    diagram: 'flowchart',
    title: 'landing.v2Design',
    summary: 'landing.v2DesignDescription',
    prompt: 'landing.v2DesignPrompt',
    labels: 'landing.v2DesignLabels'
  },
  {
    id: 'developers',
    icon: Code2,
    diagram: 'sequence',
    title: 'landing.v2Developers',
    summary: 'landing.v2DevelopersDescription',
    prompt: 'landing.v2DevelopersPrompt',
    labels: 'landing.v2DevelopersLabels'
  },
  {
    id: 'teams',
    icon: Users,
    diagram: 'kanban',
    title: 'landing.v2Teams',
    summary: 'landing.v2TeamsDescription',
    prompt: 'landing.v2TeamsPrompt',
    labels: 'landing.v2TeamsLabels'
  }
];

export function LandingUseCasesSection({ ctaHref }: { ctaHref: string }) {
  const { t } = useAppTranslation();
  const [selectedId, setSelectedId] = useState('study');
  const selected = USE_CASES.find((item) => item.id === selectedId) ?? USE_CASES[0]!;
  const SelectedIcon = selected.icon;

  return (
    <Box component="section" id="use-cases" sx={{
      scrollMarginTop: 76, position: 'relative', isolation: 'isolate', overflow: 'hidden',
      py: { xs: 9, md: 15 }, bgcolor: '#FFF0F5',
      backgroundImage: 'radial-gradient(circle, rgba(230,30,115,0.11) 1px, transparent 1px)',
      backgroundSize: '30px 30px'
    }}>
      <Box data-parallax="60" aria-hidden="true" sx={{
        position: 'absolute', width: { xs: 280, md: 510 }, height: { xs: 280, md: 510 },
        right: { xs: -175, md: -160 }, top: { xs: 0, md: -95 }, zIndex: -1,
        pointerEvents: 'none', opacity: 0.38
      }}>
        <LandingParallaxShape variant="connector" color="#E61E73" />
      </Box>
      <Box data-parallax="-42" aria-hidden="true" sx={{
        position: 'absolute', left: { xs: -135, md: -155 }, bottom: 35,
        width: { xs: 265, md: 355 }, height: { xs: 265, md: 355 },
        opacity: 0.38, pointerEvents: 'none', zIndex: -1
      }}>
        <LandingParallaxShape variant="blob" color="#D28E28" />
      </Box>
      <Container maxWidth="xl" sx={{ position: 'relative', px: { xs: 2.5, sm: 4, lg: 7 } }}>
        <Box data-reveal sx={{ maxWidth: 880 }}>
          <Typography sx={{ color: ACCENT, fontWeight: 800, letterSpacing: '0.11em', fontSize: 12, textTransform: 'uppercase' }}>
            {t('landing.v2UseCasesEyebrow')}
          </Typography>
          <Typography component="h2" sx={{ mt: 1.5, fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: { xs: 39, sm: 55, md: 72 }, letterSpacing: '-0.058em', lineHeight: 1.05, textWrap: 'balance', color: INK }}>
            {t('landing.v2UseCasesTitle')}
          </Typography>
          <Typography sx={{ mt: 2, fontSize: { xs: 15.5, md: 18 }, lineHeight: 1.65, color: MUTED }}>
            {t('landing.v2UseCasesDescription')}
          </Typography>
        </Box>

        <Box
          data-reveal
          role="group"
          aria-label={t('landing.v2UseCasesTitle')}
          sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: { xs: 1.25, sm: 2 }, mt: { xs: 4.5, md: 6 } }}
        >
          {USE_CASES.map(({ id, icon: Icon, title, summary }) => {
            const active = id === selectedId;
            return (
              <ButtonBase
                key={id}
                data-testid={`landing-v2-case-${id}`}
                aria-pressed={active}
                onClick={() => setSelectedId(id)}
                sx={{
                  textAlign: 'left',
                  alignItems: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 1, sm: 1.35 },
                  minWidth: 0,
                  p: { xs: 1.75, sm: 2.5 },
                  border: `1.5px solid ${active ? BRAND.pinkStrong : 'rgba(230,30,115,0.15)'}`,
                  borderRadius: '16px',
                  bgcolor: active ? '#FFFFFF' : 'rgba(255,255,255,0.72)',
                  boxShadow: active ? '7px 7px 0 rgba(230,30,115,0.28)' : 'none',
                  transition: 'transform 180ms ease, background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
                  '&:hover': { bgcolor: '#FFFFFF', borderColor: BRAND.pinkStrong,
                    transform: 'translateY(-3px)' },
                  '&.Mui-focusVisible': { outline: '3px solid #E61E73', outlineOffset: 2 }
                }}
              >
                <Box sx={{ width: 38, height: 38, borderRadius: '11px', display: 'grid', placeItems: 'center', bgcolor: active ? '#FFE58F' : '#FFE4EE', color: active ? ACCENT : '#7B264B' }}>
                  <Icon size={19} strokeWidth={1.85} />
                </Box>
                <Typography component="span" sx={{ fontWeight: 800, fontSize: { xs: 14.5, sm: 16 }, lineHeight: 1.2, color: INK }}>
                  {t(title)}
                </Typography>
                <Typography component="span" sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 13.5, color: MUTED, lineHeight: 1.5 }}>
                  {t(summary)}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>

        <Box
          data-reveal
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 0.86fr) minmax(0, 1.14fr)' },
            border: '2px solid #171A22',
            borderRadius: { xs: '18px', md: '28px' },
            overflow: 'hidden',
            boxShadow: '14px 16px 0 rgba(23,26,34,0.12), 0 24px 60px rgba(23,26,34,0.10)',
            bgcolor: '#FFFFFF'
          }}
        >
          <Stack spacing={2.4} sx={{ px: { xs: 2.5, md: 4.5 }, py: { xs: 3, md: 5 }, minWidth: 0, justifyContent: 'center' }}>
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <Box sx={{ borderRadius: '11px', width: 42, height: 42, bgcolor: '#F0E5D7', color: ACCENT, display: 'grid', placeItems: 'center' }}>
                <SelectedIcon size={21} strokeWidth={1.9} />
              </Box>
              <Typography component="h3" data-testid="landing-v2-selected-title" sx={{ fontWeight: 800, fontSize: 21, color: INK }}>
                {t(selected.title)}
              </Typography>
            </Stack>
            <Typography sx={{ color: MUTED, lineHeight: 1.6, fontSize: 15.5 }}>{t(selected.summary)}</Typography>
            <Box sx={{ p: 2.5, borderRadius: '14px', bgcolor: '#FFF9F6', border: '1px solid #E4E7DF' }}>
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1.2 }}>
                <Sparkles size={16} color={ACCENT} />
                <Typography sx={{ color: ACCENT, fontSize: 12.5, fontWeight: 800 }}>{t('landing.v2PromptLabel')}</Typography>
              </Stack>
              <Typography data-testid="landing-v2-prompt" sx={{ fontSize: 16, lineHeight: 1.5, fontWeight: 600, color: INK }}>
                {t(selected.prompt)}
              </Typography>
            </Box>
            <Button component={Link} href={ctaHref} color="secondary" variant="contained" endIcon={<ArrowRight size={17} />} sx={{ alignSelf: 'flex-start', borderRadius: '10px', px: 2.75, py: 1.1, bgcolor: INK, color: '#FFFFFF', '&:hover': { bgcolor: '#344238' } }}>
              {t('landing.v2TryBoard')}
            </Button>
            <Typography sx={{ fontSize: 12, lineHeight: 1.45, color: '#77737A' }}>
              {t('landing.v2PreviewNote')}
            </Typography>
          </Stack>

          <Box
            data-testid="landing-v2-use-case-preview"
            data-kind={selected.diagram}
            role="img"
            aria-label={t('landing.makePreviewLabel', { type: t(selected.title) })}
            sx={{
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2, sm: 3, md: 4 },
              bgcolor: '#E6DDFF',
              backgroundImage: 'radial-gradient(rgba(46,35,85,0.18) 0.9px, transparent 0.9px)',
              backgroundSize: '20px 20px',
              borderLeft: { md: '1px solid #DDE4D9' },
              borderTop: { xs: '1px solid #DDE4D9', md: 'none' }
            }}
          >
            <Box key={selected.id} sx={{ width: '100%', maxWidth: 570, p: { xs: 1, sm: 2 }, bgcolor: '#FFFFFF', border: '1px solid #DEE4DA', borderRadius: '14px', boxShadow: '0 20px 48px rgba(25, 25, 31, 0.10)', animation: 'v2PreviewEnter 280ms ease-out', '@keyframes v2PreviewEnter': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.5, py: 1, borderBottom: '1px solid #E3E8DF' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: INK }}>DouBoard</Typography>
                <Typography sx={{ fontSize: 11, color: MUTED }}>{t('landing.v2VisualPreview')}</Typography>
              </Stack>
              <Box sx={{ aspectRatio: '400 / 260', px: 1.5, py: 1 }}>
                <DiagramPreview kind={selected.diagram} labels={t(selected.labels).split('|')} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
